import React, { useState } from 'react';
import { PlusIcon, ServerIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';

interface NFSInstance {
  id: string;
  name: string;
  host: string;
  port: number;
  apiKey?: string;
  status?: string;
  lastChecked?: string;
}

interface NFSInstancesListProps {
  instances: NFSInstance[];
  onAddInstance: () => void;
  onEditInstance: (id: string) => void;
  onDeleteInstance: (id: string) => void;
}

const NFSInstancesList: React.FC<NFSInstancesListProps> = ({
  instances,
  onAddInstance,
  onEditInstance,
  onDeleteInstance
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const filteredInstances = instances.filter(instance =>
    instance.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    instance.host.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status?: string) => {
    if (status === 'Running') return 'bg-green-500';
    if (status === 'unauthorized') return 'bg-yellow-400';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-zinc-100 flex items-center">
          <ServerIcon className="h-6 w-6 mr-2 text-indigo-500" />
          NFS Servers
        </h2>
        <button
          type="button"
          onClick={onAddInstance}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          New Server
        </button>
      </div>
      {/* Description */}
      <div className="bg-zinc-800/50 rounded-lg p-4 text-sm text-zinc-300">
        <p>Servers are NFS hosts that you can manage and deploy configurations to. Each server represents a physical or virtual machine running NFS.</p>
        <p className="mt-2">You can add multiple servers and monitor their status from here.</p>
      </div>
      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search servers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
        />
      </div>
      {/* Servers list */}
      {instances.length === 0 ? (
        <div className="bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700 p-8 text-center">
          <ServerIcon className="mx-auto h-12 w-12 text-zinc-500" />
          <h3 className="mt-2 text-sm font-medium text-zinc-400">No servers found</h3>
          <p className="mt-1 text-sm text-zinc-500">Get started by adding a new NFS server.</p>
          <div className="mt-6">
            <button
              type="button"
              onClick={onAddInstance}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
            >
              <PlusIcon className="h-4 w-4 mr-1.5" />
              New Server
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/30">
          <table className="min-w-full divide-y divide-zinc-800">
            <thead className="bg-zinc-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Server Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Host</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Port</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Status</th>
                <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800 bg-transparent">
              {filteredInstances.map((instance) => (
                <tr key={instance.id} className="hover:bg-zinc-800/30 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-200">
                    <div className="flex items-center">
                      <ServerIcon className="h-5 w-5 mr-3 text-zinc-500" />
                      {instance.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">{instance.host}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">{instance.port}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center">
                      <span className={`h-2.5 w-2.5 rounded-full mr-2 ${getStatusColor(instance.status)}`}></span>
                      <span className="text-sm text-zinc-300">
                        {instance.status === 'Running' && 'Running'}
                        {instance.status === 'unauthorized' && (
                          <span className="text-yellow-400 font-semibold">Unauthorized</span>
                        )}
                        {(!instance.status || (instance.status !== 'Running' && instance.status !== 'unauthorized')) && 'Stopped'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => onEditInstance(instance.id)}
                        className="text-indigo-400 hover:text-indigo-300 transition-colors duration-150"
                        title="Edit Server"
                      >
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(instance.id)}
                        className="text-red-400 hover:text-red-300 transition-colors duration-150"
                        title="Delete Server"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity" onClick={() => setConfirmDelete(null)}></div>
          <div className="bg-zinc-900 rounded-xl shadow-2xl overflow-hidden w-full max-w-md relative z-10">
            <div className="p-6">
              <h3 className="text-lg font-medium text-zinc-100 mb-3">Confirm Delete</h3>
              <p className="text-sm text-zinc-300 mb-6">
                Are you sure you want to delete this server? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => { onDeleteInstance(confirmDelete!); setConfirmDelete(null); }}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NFSInstancesList; 